// Three.js and app-specific imports
import * as THREE from "three";
import { DrawingGlobals, VARS } from "../Global/variables";
import { createMeasurementLabels } from "./Labels";
import { GEOMETRIES, MATERIALS, COLORS } from "../Global/materials";
import { createSegmentWireframe } from "../Global/fatLines";
import { calculatePitch, getAzimuth } from "./AreaPitchAzimu";

const headingDegrees = (point1, point2) => {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    const deg = THREE.MathUtils.radToDeg(Math.atan2(dx, dy));
    return deg < 0 ? deg + 360 : deg;
};

const slopeDegrees = (point1, point2) => {
    const run = Math.hypot(point2.x - point1.x, point2.y - point1.y);
    const rise = point2.z - point1.z;
    if (run === 0 && rise === 0) return 0;
    return THREE.MathUtils.radToDeg(Math.atan2(rise, run));
};

/**
 * Interior corner angle at `corner`, between the two edges meeting there, in degrees.
 */
const cornerDegrees = (prev, corner, next) => {
    const a = new THREE.Vector3().subVectors(prev, corner);
    const b = new THREE.Vector3().subVectors(next, corner);
    if (a.lengthSq() === 0 || b.lengthSq() === 0) return 0;
    return THREE.MathUtils.radToDeg(a.angleTo(b));
};

const logDrawingAngles = (coordinates, edgeData) => {
    try {
        const n = coordinates.length;
        const edges = edgeData.map((edge, i) => {
            const prev = coordinates[(i - 1 + n) % n];
            return {
                edge: i,
                lengthFt: Number(edge.measurement?.toFixed?.(2) ?? edge.measurement),
                heading: `${headingDegrees(edge.point1, edge.point2).toFixed(2)}°`,
                slope: `${slopeDegrees(edge.point1, edge.point2).toFixed(2)}°`,
                corner: `${cornerDegrees(prev, edge.point1, edge.point2).toFixed(2)}°`,
            };
        });

        let pitch = null;
        let azimuth = null;
        if (edgeData.length >= 3) {
            const res = calculatePitch(edgeData);
            if (res && res.pitch_angle !== undefined) {
                pitch = `${res.pitch_angle}°`;
                try {
                    azimuth = `${getAzimuth(res.p2, res.p1).toFixed(2)}°`;
                } catch (e) {
                    azimuth = "n/a (no project CRS)";
                }
            }
        }

        console.log("Drawing edges", { vertices: n, pitch, azimuth, edges });
    } catch (e) {
        console.warn("Drawing edges — angle log failed:", e);
    }
};

/**
 * Creates a visual vertex (a small circle) at the given position.
 *
 * @param {THREE.Vector3} position - The position to place the vertex.
 * @returns {THREE.Mesh} A mesh representing a polygon vertex.
 */
export const createVertex = (position) => {
    const circle = new THREE.Mesh(
        GEOMETRIES.polygonVertex,
        MATERIALS.polygonVertex.clone()
    );
    // A lone vertex has no angle yet -- the corner/heading degrees are logged once
    // the edges exist, in createEdges() below.
    console.log("Drawing vertex", {
        index: DrawingGlobals.verticesMesh?.length ?? 0,
        x: Number(position.x?.toFixed(3)),
        y: Number(position.y?.toFixed(3)),
        z: Number(position.z?.toFixed(3)),
    });
    circle.position.copy(position);
    circle.name = "vertex";
    circle.renderOrder = 1; // Ensures this renders above other objects
    return circle;
};

/**
 * Creates visual and data representations of edges between vertices.
 * Adds measurement labels, edge metadata, and setback information.
 *
 * @param {THREE.Vector3[]} coordinates - List of vertex positions.
 * @param {THREE.Scene} scene - The scene to add labels to.
 * @returns {object} An object containing:
 *   - edgeData: measurement info and IDs for each edge.
 *   - edgeWireframe: visual line segments connecting the edges.
 *   - setBackData: geometry-based setback metadata for each edge.
 */
export const createEdges = (coordinates, scene) => {
    const edgePoints = [];     // Used for visual wireframe
    const edgeData = [];       // Stores measurement and vertex ID metadata
    const setBackData = [];    // Used for offset/setback logic

    const vertices = DrawingGlobals.verticesMesh;

    for (let i = 0; i < coordinates.length; i++) {
        let index = (i + 1) % coordinates.length;
        const point1 = coordinates[i];
        const point2 = coordinates[index];

        edgePoints.push(point1, point2);

        // Create label and measurement for this edge
        let data = createMeasurementLabels(point1, point2, i, scene);
        data.vertexId1 = vertices[i].uuid;
        data.vertexId2 = vertices[index].uuid;

        // Store setback data for edge
        const setback = {
            vertexId1: vertices[i].uuid,
            vertexId2: vertices[index].uuid,
            value: 0,
            point1: point1.clone(),
            point2: point2.clone()
        };

        setBackData.push(setback);
        edgeData.push(data);
    }

    logDrawingAngles(coordinates, edgeData);

    // Increment the global measurement label counter
    VARS.measurementLabelCounter++;

    const edgeWireframe = createSegmentWireframe(edgePoints, COLORS.white);

    return {
        edgeData,
        edgeWireframe,
        setBackData
    };
};
